import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { create } from 'zustand';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useJira } from './JiraSettings';
import { Modal } from './RoomDialogs';

interface Draft {
  /** From a message: its thread says which issue was opened. */
  messageId?: string;
  /** From /jira in a channel: the channel says it. */
  channelId?: string;
  projectKey?: string;
  summary: string;
  description: string;
}

/** "Jira işi aç" (docs/plans/2026-11-04-jira-design.md §4), from a message's toolbar or `/jira`; rendered once by Shell. */
export const useJiraCreate = create<{ draft: Draft | null; open(draft: Draft): void; close(): void }>()((set) => ({
  draft: null,
  open: (draft) => set({ draft }),
  close: () => set({ draft: null }),
}));

const LAST_PROJECT = 'novachat.jira.project';
const field = 'h-[42px] rounded-md border border-line-strong bg-surface-2 px-3 text-base text-fg-1 focus:border-brand-400 focus:outline-none sm:text-[15px]';

export function CreateJiraIssueDialog() {
  const draft = useJiraCreate((s) => s.draft);
  return draft ? <Form draft={draft} /> : null;
}

function Form({ draft }: { draft: Draft }) {
  const jira = useJira();
  const projects = useQuery({ queryKey: ['jira-projects'], queryFn: () => client.api.jiraProjects(), enabled: !!jira.data?.connection, staleTime: 5 * 60_000 });
  const [projectKey, setProjectKey] = useState(() => {
    if (draft.projectKey) return draft.projectKey;
    try {
      return localStorage.getItem(LAST_PROJECT) ?? '';
    } catch {
      return '';
    }
  });
  const project = projects.data?.find((p) => p.key === projectKey) ?? null;
  const [issueType, setIssueType] = useState('');
  const [summary, setSummary] = useState(draft.summary.slice(0, 255));
  const [description, setDescription] = useState(draft.description);
  const [assignToMe, setAssignToMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (project && !project.issueTypes.includes(issueType)) setIssueType(project.issueTypes[0] ?? '');
  }, [project, issueType]);

  async function submit(event: FormEvent, close: () => void) {
    event.preventDefault();
    if (!project) return setError('Bir proje seç.');
    if (!summary.trim()) return setError('Başlık boş olamaz.');
    setBusy(true);
    setError(null);
    try {
      await client.api.createJiraIssue({
        projectKey: project.key,
        issueType,
        summary: summary.trim(),
        description,
        assignToMe,
        ...(draft.messageId ? { messageId: draft.messageId } : {}),
        ...(draft.channelId ? { channelId: draft.channelId } : {}),
      });
      try {
        localStorage.setItem(LAST_PROJECT, project.key);
      } catch {
        // a remembered project is only a convenience
      }
      close();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Jira işi aç" onClose={() => useJiraCreate.getState().close()}>
      {(close) =>
        !jira.data?.connection ? (
          <p className="m-0 text-sm text-fg-2">Önce Ayarlar › Bağlantılar’dan Jira hesabını bağla.</p>
        ) : (
          <form onSubmit={(e) => void submit(e, close)} className="flex flex-col gap-3" noValidate>
            <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-fg-2">Proje</span>
                <select value={projectKey} onChange={(e) => setProjectKey(e.target.value)} className={field}>
                  <option value="">{projects.isPending ? 'Yükleniyor…' : 'Proje seç'}</option>
                  {projects.data?.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.key} · {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-fg-2">Tür</span>
                <select value={issueType} onChange={(e) => setIssueType(e.target.value)} disabled={!project} className={field}>
                  {project?.issueTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-fg-2">Başlık</span>
              <input autoFocus value={summary} maxLength={255} onChange={(e) => setSummary(e.target.value)} className={field} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-fg-2">Açıklama</span>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="resize-y rounded-md border border-line-strong bg-surface-2 px-3 py-2 text-base text-fg-1 focus:border-brand-400 focus:outline-none sm:text-[15px]"
              />
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={assignToMe} onChange={(e) => setAssignToMe(e.target.checked)} className="size-4 accent-brand-500" />
              Bana ata
            </label>
            {error && (
              <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={close} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
                Vazgeç
              </button>
              <button type="submit" disabled={busy} className="nc-press nc-cta h-10 rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white disabled:opacity-60">
                İşi aç
              </button>
            </div>
          </form>
        )
      }
    </Modal>
  );
}
